import { trackToolAction } from "../../../utils/analytics";
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { apiService } from "../../../api/api";
import type { MusicVideoMetadataMismatchAudit } from "../../../utils/@types";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { InputText } from "primereact/inputtext";
import { Dialog } from "primereact/dialog";
import { MultiSelect } from "primereact/multiselect";
import MultiSearch from "../../../Components/SearchInputs/MultiSearch/MultiSearch";
import "../../YouTubeScorecard/Components/FilterBars/auditFilters.css";
import "../../YouTubeScorecard/Components/Tables/tables.css";
import { Card } from "primereact/card";
import {
  buildQueryString,
  parseStringArrayParam,
  parseStringParam,
} from "../../../utils/urlFilters";
import { FULL_PAGE_TABLE_SCROLL_HEIGHT } from "../../../config/tableLayout";
import { withExportDate } from "../../../utils/exportFileName";

interface ColumnConfig {
  field: string;
  header: string;
  sortable?: boolean;
  visible: boolean;
}

const FilterBar = ({
  filters,
  setFilters,
  unfilteredData,
  onSearch,
  onReset,
  onExport,
  onColumnVisibility,
}: any) => {
  const normaliseStr = (s: any): string =>
    String(s ?? "")
      .normalize("NFC")
      .replace(/[\u00A0\u200B\u200C\u200D\uFEFF\u2060]/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const musicVideoTitleOptions = Array.from(
    new Set(unfilteredData.map((item: any) => normaliseStr(item.musicVideoTitle)).filter(Boolean))
  ).map((value) => ({ label: value, value }));

  const soundRecordingTitleOptions = Array.from(
    new Set(unfilteredData.map((item: any) => normaliseStr(item.soundRecordingTitle)).filter(Boolean))
  ).map((value) => ({ label: value, value }));

  const musicVideoArtistOptions = Array.from(
    new Set(unfilteredData.map((item: any) => normaliseStr(item.musicVideoArtist)).filter(Boolean))
  ).map((value) => ({ label: value, value }));

  const soundRecordingArtistOptions = Array.from(
    new Set(unfilteredData.map((item: any) => normaliseStr(item.soundRecordingArtist)).filter(Boolean))
  ).map((value) => ({ label: value, value }));

  const musicVideoLabelOptions = Array.from(
    new Set(unfilteredData.map((item: any) => normaliseStr(item.musicVideoLabel)).filter(Boolean))
  ).map((value) => ({ label: value, value }));

  const soundRecordingLabelOptions = Array.from(
    new Set(unfilteredData.map((item: any) => normaliseStr(item.soundRecordingLabel)).filter(Boolean))
  ).map((value) => ({ label: value, value }));

  return (
    <>
      <div className="grid-container" style={{ marginTop: "-1rem" }}>
        <div className="filter-item">
          <MultiSearch
            selectedItems={filters.musicVideoAssetId}
            value={(items) =>
              setFilters((prev: any) => ({ ...prev, musicVideoAssetId: items }))
            }
            label={"Music Video Asset ID"}
            parseType="both"
          />
        </div>
        <div className="filter-item">
          <MultiSearch
            selectedItems={filters.soundRecordingAssetId}
            value={(items) =>
              setFilters((prev: any) => ({
                ...prev,
                soundRecordingAssetId: items,
              }))
            }
            label={"Sound Recording Asset ID"}
            parseType="both"
          />
        </div>
        <div className="filter-item">
          <MultiSearch
            selectedItems={filters.musicVideoIsrc}
            value={(items) =>
              setFilters((prev: any) => ({ ...prev, musicVideoIsrc: items }))
            }
            label={"Music Video ISRC"}
            parseType="both"
          />
        </div>
        <div className="filter-item">
          <MultiSearch
            selectedItems={filters.soundRecordingIsrc}
            value={(items) =>
              setFilters((prev: any) => ({
                ...prev,
                soundRecordingIsrc: items,
              }))
            }
            label={"Sound Recording ISRC"}
            parseType="both"
          />
        </div>
        <div className="filter-item">
          <text
            style={{ color: "grey", fontSize: "small", marginLeft: "0.4rem" }}
          >
            Music Video Title
          </text>
          <MultiSelect
            value={filters.musicVideoTitle ? [filters.musicVideoTitle] : []}
            options={musicVideoTitleOptions}
            onChange={(e) =>
              setFilters((prev: any) => ({
                ...prev,
                musicVideoTitle: e.value[0] || "",
              }))
            }
            maxSelectedLabels={1}
            optionLabel="label"
            style={{ width: "90%", borderRadius: "5px" }}
            filter
            showClear
          />
        </div>
        <div className="filter-item">
          <text
            style={{ color: "grey", fontSize: "small", marginLeft: "0.4rem" }}
          >
            Sound Recording Title
          </text>
          <MultiSelect
            value={
              filters.soundRecordingTitle ? [filters.soundRecordingTitle] : []
            }
            options={soundRecordingTitleOptions}
            onChange={(e) =>
              setFilters((prev: any) => ({
                ...prev,
                soundRecordingTitle: e.value[0] || "",
              }))
            }
            maxSelectedLabels={1}
            optionLabel="label"
            style={{ width: "90%", borderRadius: "5px" }}
            filter
            showClear
          />
        </div>
        <div className="filter-item">
          <text
            style={{ color: "grey", fontSize: "small", marginLeft: "0.4rem" }}
          >
            Music Video Artist
          </text>
          <MultiSelect
            value={filters.musicVideoArtist ? [filters.musicVideoArtist] : []}
            options={musicVideoArtistOptions}
            onChange={(e) =>
              setFilters((prev: any) => ({
                ...prev,
                musicVideoArtist: e.value[0] || "",
              }))
            }
            maxSelectedLabels={1}
            optionLabel="label"
            style={{ width: "90%", borderRadius: "5px" }}
            filter
            showClear
          />
        </div>
        <div className="filter-item">
          <text
            style={{ color: "grey", fontSize: "small", marginLeft: "0.4rem" }}
          >
            Sound Recording Artist
          </text>
          <MultiSelect
            value={
              filters.soundRecordingArtist ? [filters.soundRecordingArtist] : []
            }
            options={soundRecordingArtistOptions}
            onChange={(e) =>
              setFilters((prev: any) => ({
                ...prev,
                soundRecordingArtist: e.value[0] || "",
              }))
            }
            maxSelectedLabels={1}
            optionLabel="label"
            style={{ width: "90%", borderRadius: "5px" }}
            filter
            showClear
          />
        </div>
        <div className="filter-item">
          <text
            style={{ color: "grey", fontSize: "small", marginLeft: "0.4rem" }}
          >
            Music Video Label
          </text>
          <MultiSelect
            value={filters.musicVideoLabel ? [filters.musicVideoLabel] : []}
            options={musicVideoLabelOptions}
            onChange={(e) =>
              setFilters((prev: any) => ({
                ...prev,
                musicVideoLabel: e.value[0] || "",
              }))
            }
            maxSelectedLabels={1}
            optionLabel="label"
            style={{ width: "90%", borderRadius: "5px" }}
            filter
            showClear
          />
        </div>
        <div className="filter-item">
          <text
            style={{ color: "grey", fontSize: "small", marginLeft: "0.4rem" }}
          >
            Sound Recording Label
          </text>
          <MultiSelect
            value={
              filters.soundRecordingLabel ? [filters.soundRecordingLabel] : []
            }
            options={soundRecordingLabelOptions}
            onChange={(e) =>
              setFilters((prev: any) => ({
                ...prev,
                soundRecordingLabel: e.value[0] || "",
              }))
            }
            maxSelectedLabels={1}
            optionLabel="label"
            style={{ width: "90%", borderRadius: "5px" }}
            filter
            showClear
          />
        </div>
      </div>
      <div className="grid-container">
        <div className="flex-row grid gap-5" style={{ marginLeft: "0.1vh" }}>
          <div className="flex-2" style={{ paddingTop: "1.5rem" }}>
            <Button label="Search" icon="pi pi-search" onClick={onSearch} />
          </div>
          <div className="flex-2" style={{ paddingTop: "1.5rem" }}>
            <Button
              label="Reset"
              icon="pi pi-replay"
              severity="danger"
              onClick={onReset}
            />
          </div>
          <div className="flex-2" style={{ paddingTop: "1.5rem" }}>
            <Button
              label="Export"
              icon="pi pi-file"
              onClick={onExport}
              severity="success"
            />
          </div>
          <div className="flex-2" style={{ paddingTop: "1.5rem" }}>
            <Button
              label="Columns"
              icon="pi pi-columns"
              onClick={onColumnVisibility}
              severity="secondary"
            />
          </div>
        </div>
      </div>
    </>
  );
};

const MusicVideoMetadataMismatchAuditTable: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialFilters = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      musicVideoAssetId: parseStringArrayParam(params, "musicVideoAssetId"),
      soundRecordingAssetId: parseStringArrayParam(params, "soundRecordingAssetId"),
      musicVideoIsrc: parseStringArrayParam(params, "musicVideoIsrc"),
      soundRecordingIsrc: parseStringArrayParam(params, "soundRecordingIsrc"),
      musicVideoTitle: parseStringParam(params, "musicVideoTitle"),
      soundRecordingTitle: parseStringParam(params, "soundRecordingTitle"),
      musicVideoArtist: parseStringParam(params, "musicVideoArtist"),
      soundRecordingArtist: parseStringParam(params, "soundRecordingArtist"),
      musicVideoLabel: parseStringParam(params, "musicVideoLabel"),
      soundRecordingLabel: parseStringParam(params, "soundRecordingLabel"),
    };
  }, [location.search]);

  const dt = useRef<DataTable<MusicVideoMetadataMismatchAudit[]>>(null);
  const initialFiltersApplied = useRef(false);
  const [rows, setRows] = useState<MusicVideoMetadataMismatchAudit[]>([]);
  const [unfilteredData, setUnfilteredData] = useState<
    MusicVideoMetadataMismatchAudit[]
  >([]);
  const [filteredRows, setFilteredRows] = useState<
    MusicVideoMetadataMismatchAudit[]
  >([]);
  const [columnVisibilityDialog, setColumnVisibilityDialog] = useState(false);

  const [filters, setFilters] = useState<any>(initialFilters);

  const [columnConfig, setColumnConfig] = useState<ColumnConfig[]>([
    { field: "musicVideoAssetId", header: "Music Video Asset ID", visible: true },
    {
      field: "soundRecordingAssetId",
      header: "Sound Recording Asset ID",
      visible: true,
    },
    { field: "musicVideoIsrc", header: "Music Video ISRC", visible: true },
    {
      field: "soundRecordingIsrc",
      header: "Sound Recording ISRC",
      visible: true,
    },
    { field: "musicVideoTitle", header: "Music Video Title", visible: true },
    {
      field: "soundRecordingTitle",
      header: "Sound Recording Title",
      visible: true,
    },
    { field: "musicVideoArtist", header: "Music Video Artist", visible: true },
    {
      field: "soundRecordingArtist",
      header: "Sound Recording Artist",
      visible: true,
    },
    { field: "musicVideoLabel", header: "Music Video Label", visible: true },
    {
      field: "soundRecordingLabel",
      header: "Sound Recording Label",
      visible: true,
    },
    { field: "checked", header: "Checked", visible: true },
    { field: "notes", header: "Notes", visible: true },
  ]);

  useEffect(() => {
    apiService.musicAssetAudit
      .getMusicVideoMetadataMismatches({})
      .then((data: any) => {
        setUnfilteredData(data);
        setRows(data);
        setFilteredRows(data);
      });
  }, []);

  const handleSearch = useCallback(() => {
    trackToolAction("DataHealthTool", "search");
    let filtered = rows;

    if (filters.musicVideoAssetId?.length)
      filtered = filtered.filter((r) =>
        filters.musicVideoAssetId.some((v: string) =>
          r.musicVideoAssetId?.toLowerCase().includes(v.toLowerCase())
        )
      );
    if (filters.soundRecordingAssetId?.length)
      filtered = filtered.filter((r) =>
        filters.soundRecordingAssetId.some((v: string) =>
          r.soundRecordingAssetId?.toLowerCase().includes(v.toLowerCase())
        )
      );
    if (filters.musicVideoIsrc?.length)
      filtered = filtered.filter((r) =>
        filters.musicVideoIsrc.some((v: string) =>
          r.musicVideoIsrc?.toLowerCase().includes(v.toLowerCase())
        )
      );
    if (filters.soundRecordingIsrc?.length)
      filtered = filtered.filter((r) =>
        filters.soundRecordingIsrc.some((v: string) =>
          r.soundRecordingIsrc?.toLowerCase().includes(v.toLowerCase())
        )
      );
    if (filters.musicVideoTitle)
      filtered = filtered.filter((r) =>
        r.musicVideoTitle
          ?.toLowerCase()
          .includes(filters.musicVideoTitle.toLowerCase())
      );
    if (filters.soundRecordingTitle)
      filtered = filtered.filter((r) =>
        r.soundRecordingTitle
          ?.toLowerCase()
          .includes(filters.soundRecordingTitle.toLowerCase())
      );
    if (filters.musicVideoArtist)
      filtered = filtered.filter((r) =>
        r.musicVideoArtist
          ?.toLowerCase()
          .includes(filters.musicVideoArtist.toLowerCase())
      );
    if (filters.soundRecordingArtist)
      filtered = filtered.filter((r) =>
        r.soundRecordingArtist
          ?.toLowerCase()
          .includes(filters.soundRecordingArtist.toLowerCase())
      );
    if (filters.musicVideoLabel)
      filtered = filtered.filter((r) =>
        r.musicVideoLabel
          ?.toLowerCase()
          .includes(filters.musicVideoLabel.toLowerCase())
      );
    if (filters.soundRecordingLabel)
      filtered = filtered.filter((r) =>
        r.soundRecordingLabel
          ?.toLowerCase()
          .includes(filters.soundRecordingLabel.toLowerCase())
      );

    setFilteredRows(filtered);
    const query = buildQueryString({
      musicVideoAssetId: filters.musicVideoAssetId,
      soundRecordingAssetId: filters.soundRecordingAssetId,
      musicVideoIsrc: filters.musicVideoIsrc,
      soundRecordingIsrc: filters.soundRecordingIsrc,
      musicVideoTitle: filters.musicVideoTitle,
      soundRecordingTitle: filters.soundRecordingTitle,
      musicVideoArtist: filters.musicVideoArtist,
      soundRecordingArtist: filters.soundRecordingArtist,
      musicVideoLabel: filters.musicVideoLabel,
      soundRecordingLabel: filters.soundRecordingLabel,
    });
    navigate({ search: query }, { replace: true });
  }, [filters, navigate, rows]);

  const handleReset = () => {
    setFilters({
      musicVideoAssetId: [],
      soundRecordingAssetId: [],
      musicVideoIsrc: [],
      soundRecordingIsrc: [],
      musicVideoTitle: "",
      soundRecordingTitle: "",
      musicVideoArtist: "",
      soundRecordingArtist: "",
      musicVideoLabel: "",
      soundRecordingLabel: "",
    });
    setFilteredRows(rows);
    navigate({ search: "" }, { replace: true });
  };

  useEffect(() => {
    const hasInitialFilters = Object.values(initialFilters).some((v) =>
      Array.isArray(v) ? v.length > 0 : Boolean(v)
    );
    if (!hasInitialFilters || rows.length === 0 || initialFiltersApplied.current)
      return;
    handleSearch();
    initialFiltersApplied.current = true;
  }, [initialFilters, rows, handleSearch]);

  const onRowEditComplete = (e: any) => {
    let _rows = [...rows];
    let { newData, index } = e;
    _rows[index] = newData;
    setRows(_rows);
    apiService.musicAssetAudit.updateMusicVideoMetadataMismatch(newData);
  };

  const checkedBodyTemplate = (rowData: MusicVideoMetadataMismatchAudit) => (
    <Checkbox
      onChange={(e) => {
        let _rows = [...rows];
        const index = _rows.findIndex(
          (r) => r.musicVideoAssetId === rowData.musicVideoAssetId
        );
        _rows[index].checked = e.checked;
        setRows(_rows);
        apiService.musicAssetAudit.updateMusicVideoMetadataMismatch({
          musicVideoAssetId: rowData.musicVideoAssetId,
          checked: e.checked,
        });
      }}
      checked={rowData.checked ?? false}
    />
  );

  const notesEditor = (options: any) => (
    <InputText
      type="text"
      value={options.value}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
        options.editorCallback && options.editorCallback(e.target.value)
      }
    />
  );

  const exportCSV = () => {
    trackToolAction("DataHealthTool", "export");
    dt?.current?.exportCSV();
  };

  const handleColumnVisibilityChange = (field: string, visible: boolean) => {
    setColumnConfig((prev) =>
      prev.map((col) => (col.field === field ? { ...col, visible } : col))
    );
  };

  const resetColumnVisibility = () => {
    setColumnConfig((prev) => prev.map((col) => ({ ...col, visible: true })));
  };

  const isColumnVisible = (field: string): boolean => {
    const column = columnConfig.find((col) => col.field === field);
    return column?.visible === true || false;
  };

  return (
    <div style={{ width: "100%" }}>
      <Dialog
        header="Column Visibility"
        visible={columnVisibilityDialog}
        style={{ width: "40vw" }}
        onHide={() => setColumnVisibilityDialog(false)}
      >
        <div className="grid">
          {columnConfig.map((column) => (
            <div key={column.field} className="col-12 md:col-6 lg:col-4">
              <div className="flex align-items-center gap-2 p-2">
                <Checkbox
                  checked={column.visible}
                  onChange={(e) =>
                    handleColumnVisibilityChange(
                      column.field,
                      e.checked || false
                    )
                  }
                />
                <label className="text-sm">{column.header}</label>
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-content-end gap-2 mt-3">
          <Button
            label="Reset All"
            icon="pi pi-refresh"
            onClick={resetColumnVisibility}
            severity="secondary"
            outlined
          />
          <Button
            label="Close"
            icon="pi pi-times"
            onClick={() => setColumnVisibilityDialog(false)}
          />
        </div>
      </Dialog>

      <Card
        className="flex-column md:flex-col gap-3"
        style={{
          marginTop: "0.3rem",
          marginBottom: "2rem",
          boxShadow: "0px 4px 11px rgba(0, 0, 0, 0.1)",
          borderRadius: "7px",
        }}
      >
        <FilterBar
          filters={filters}
          setFilters={setFilters}
          unfilteredData={unfilteredData}
          onSearch={handleSearch}
          onReset={handleReset}
          onExport={exportCSV}
          onColumnVisibility={() => setColumnVisibilityDialog(true)}
        />
        <DataTable
          ref={dt}
          exportFilename={withExportDate("Music Video Metadata Audit")}
          value={filteredRows}
          size="normal"
          paginator
          rows={50}
          paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
          showGridlines
          stripedRows
          removableSort
          scrollable
          scrollHeight={FULL_PAGE_TABLE_SCROLL_HEIGHT}
          sortMode="multiple"
          tableStyle={{ width: "auto", fontSize: "0.8rem" }}
          className="custom-datatable-paginator"
          editMode="row"
          onRowEditComplete={onRowEditComplete}
        >
          {isColumnVisible("musicVideoAssetId") && (
            <Column
              field="musicVideoAssetId"
              header="Music Video Asset ID"
              sortable
            />
          )}
          {isColumnVisible("soundRecordingAssetId") && (
            <Column
              field="soundRecordingAssetId"
              header="Sound Recording Asset ID"
              sortable
            />
          )}
          {isColumnVisible("musicVideoIsrc") && (
            <Column field="musicVideoIsrc" header="Music Video ISRC" sortable />
          )}
          {isColumnVisible("soundRecordingIsrc") && (
            <Column
              field="soundRecordingIsrc"
              header="Sound Recording ISRC"
              sortable
            />
          )}
          {isColumnVisible("musicVideoTitle") && (
            <Column
              field="musicVideoTitle"
              header="Music Video Title"
              sortable
            />
          )}
          {isColumnVisible("soundRecordingTitle") && (
            <Column
              field="soundRecordingTitle"
              header="Sound Recording Title"
              sortable
            />
          )}
          {isColumnVisible("musicVideoArtist") && (
            <Column
              field="musicVideoArtist"
              header="Music Video Artist"
              sortable
            />
          )}
          {isColumnVisible("soundRecordingArtist") && (
            <Column
              field="soundRecordingArtist"
              header="Sound Recording Artist"
              sortable
            />
          )}
          {isColumnVisible("musicVideoLabel") && (
            <Column
              field="musicVideoLabel"
              header="Music Video Label"
              sortable
            />
          )}
          {isColumnVisible("soundRecordingLabel") && (
            <Column
              field="soundRecordingLabel"
              header="Sound Recording Label"
              sortable
            />
          )}
          <Column
            rowEditor
            headerStyle={{ width: "10%", minWidth: "8rem" }}
            bodyStyle={{ textAlign: "center" }}
          />
          {isColumnVisible("checked") && (
            <Column body={checkedBodyTemplate} header="Checked" />
          )}
          {isColumnVisible("notes") && (
            <Column field="notes" header="Notes" editor={notesEditor} sortable />
          )}
        </DataTable>
      </Card>
    </div>
  );
};

export default MusicVideoMetadataMismatchAuditTable;
